/**
 * PromoCodeField — manual promo-code entry on the checkout steps (D21
 * amendment: capture = tenant link OR typed input, one state home).
 *
 * A thin wrapper: the disclosure itself is the shared
 * `@butter/ui/domains/subscriptions` `PromoCodeDisclosure` (native `<details>` +
 * uncontrolled form — mobile's confirm page mounts the same pattern); this
 * file owns only the CAPTURE wiring — Apply feeds the capture context's
 * `setPromoCode` (which trims, stashes to sessionStorage, and scrubs an
 * overridden `?promo=`), so the code lands in the quote key and the requote
 * does everything else (discount row, chip, refusal).
 *
 * Mounted directly above the price summary in BOTH SubscriptionReviewStep
 * and FinishAccountStep so entry sits beside the money it changes. No
 * `refusal` prop on purpose: the steps' existing refusal `PromoNotice` owns
 * that surface (one amber box, not two). The disclosure defaults open when
 * a code is already captured (link or a previous entry), so a refused
 * code's input stays visible next to the notice explaining it.
 *
 * @module pages/subscription/steps/PromoCodeField
 */

import { PromoCodeDisclosure } from '@butter/ui/domains/subscriptions';
import type { ApiPurchaseQuotePromotion } from '@lgc/database/types/domains/subscription';
import { useSubscriptionPurchase } from '~/services/internalApi/subscription';

export function PromoCodeField({
  promotion,
}: {
  /** The quote's promotion block — `applied: true` renders the applied state. */
  promotion?: ApiPurchaseQuotePromotion;
}) {
  const { promoCode, setPromoCode, clearPromoCode } = useSubscriptionPurchase();

  return (
    <PromoCodeDisclosure
      defaultOpen={Boolean(promoCode)}
      promoCode={promoCode}
      applied={
        promotion?.applied && promoCode
          ? { code: promotion.code, label: promotion.label }
          : null
      }
      onApply={setPromoCode}
      onRemove={clearPromoCode}
    />
  );
}
